import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Checkbox, Input, message, Popconfirm, Space, Table, Tag, Tooltip, Typography } from "antd";
import {
  DeleteOutlined,
  MinusSquareOutlined,
  PlusOutlined,
  PlusSquareOutlined,
  SubnodeOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { ProjectLink } from "../../components/ProjectLink";
import { BOMVersionHistory } from "./BOMVersionHistory";
import { QtyInput } from "../../components/QtyInput";
import { ItemSelect } from "../../components/ItemSelect";
import { fmtItem } from "../../helpers/format";
import { activityLabel } from "../../helpers/activities";
import { useApi } from "../../helpers/useApi";
import { hasProjectGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useProjectsQuery } from "../../helpers/queries";
import type { ProjectBOMVersion, BOMActivity, BOMItem, Item } from "../../api-models";

// How each mapping state reads in the review table. Unit-incompatible is the one that
// looks resolved at a glance - the row names an item - so its label says what is wrong.
const MAPPING_TAG: Record<BOMItem["mapping"], { color: string; label: string }> = {
  MAPPED: { color: "green", label: "Mapped" },
  UNMAPPED: { color: "red", label: "Unmapped" },
  UNIT_INCOMPATIBLE: { color: "orange", label: "Unit mismatch" },
  NON_STOCK: { color: "blue", label: "Non-stock" },
  REJECTED: { color: "default", label: "Rejected" },
};

const MAPPING_OPTIONS = [
  { value: "NON_STOCK", label: "Non-stock (keep on the BOM, cannot be requested)" },
  { value: "REJECTED", label: "Rejected (not part of this BOM)" },
];

// A BOM is a work-breakdown tree with materials at its leaves, so both editing modes render
// one tree table rather than two lists: activity rows nest and expand, item rows sit under
// the activity they belong to, and every column stays in a single aligned grid.
//
// The tree is flattened here rather than handed to antd's `childrenColumnName` expansion:
// antd floats its indent and expand icon at the top of the cell, which sits visibly high
// against the 32px inputs on the row, and its expanded-keys state cannot be steered from
// outside the table (adding a child to a collapsed activity has to open it).
//
// `lines` carries the tree guides: one entry per indent slot, where slot i sits under the
// toggle of the row's ancestor at depth i. The last entry is the row's own elbow - true
// when more rows follow it under the same activity, so the vertical carries on past it -
// and the entries before it say whether that ancestor's vertical is still running.
type Row =
  | {
      key: string;
      kind: "activity";
      activity: DraftActivity;
      code: string;
      depth: number;
      lines: boolean[];
      expandable: boolean;
    }
  | { key: string; kind: "item"; activity: DraftActivity; item: DraftItem; depth: number; lines: boolean[] };

// One nesting step of the "Activity / item" column, and the width reserved for the
// expand toggle so rows without one still line up with rows that have one.
const INDENT = 24;
const TOGGLE_WIDTH = 24;
const CODE_WIDTH = 56;
// The flex gap between a row's parts, and the cell's own vertical padding - the guides
// are drawn past the padding, top and bottom, so one row's line meets the next one's.
const ROW_GAP = 8;
const CELL_PAD = 8;
const GUIDE_COLOR = "#d9d9d9";

// Draft* mirror the server's nested write payload. Ids are carried through so a save keeps
// existing rows (and their ledgers) rather than replacing them - reconcileBOMVersionItems
// matches by material, and syncBOMVersionActivities matches activities by id.
interface DraftItem {
  key: string;
  id?: number;
  itemId?: number;
  approvedQty?: number;
  remarks?: string;
  // Imported rows only: what the workbook said, for a row that resolved to no item
  // (NON_STOCK / REJECTED) and would otherwise have nothing to show.
  nameRaw?: string;
  unitRaw?: string;
  mapping?: BOMItem["mapping"];
}

// A manually-built version has no code field: an activity's code is its position in the
// tree ("2.1.3"), derived on render and on save by activityCode, so it can never drift
// from the tree it labels. An imported version carries the workbook's own codes (sparse,
// free text, e.g. "3.2" on the leaf that row of the sheet named), and shows those.
interface DraftActivity {
  key: string;
  id?: number;
  name: string;
  code?: string;
  description?: string;
  items: DraftItem[];
  children: DraftActivity[];
}

// "1", "2", then "2.1", "2.1.1" - the outline numbering a WBS is read in.
const activityCode = (parent: string, index: number) => (parent ? `${parent}.${index + 1}` : String(index + 1));

// A blank activity holds nothing at all - no name, no materials, and nothing but blanks
// below it. The root list always keeps one at the bottom as the row a new activity is
// typed into, so blanks are numbered as if they were not there and pruned before a save:
// an empty row the user never filled in should leave no trace in the document.
const isBlank = (a: DraftActivity): boolean => !a.name.trim() && a.items.length === 0 && a.children.every(isBlank);

function prune(activities: DraftActivity[]): DraftActivity[] {
  return activities.filter((a) => !isBlank(a)).map((a) => ({ ...a, children: prune(a.children) }));
}

let draftSeq = 0;
const nextKey = (prefix: string) => `${prefix}-${Date.now()}-${draftSeq++}`;
const blankActivity = (): DraftActivity => ({ key: nextKey("act"), name: "", items: [], children: [] });

function toDraftActivities(nodes: BOMActivity[] = []): DraftActivity[] {
  return nodes.map((a) => ({
    key: `a${a.id}`,
    id: a.id,
    name: a.name,
    code: a.code || undefined,
    description: a.description || undefined,
    items: (a.items ?? []).map((i) => ({
      key: `i${i.id}`,
      id: i.id,
      itemId: i.itemId ?? undefined,
      approvedQty: i.approvedQty,
      remarks: i.remarksRaw,
      nameRaw: i.itemNameRaw,
      unitRaw: i.unitRaw,
      mapping: i.mapping,
    })),
    children: toDraftActivities(a.children ?? []),
  }));
}

// leafActivityKeys: the activities that hold items directly. An imported workbook is a few
// dozen work titles over hundreds of areas holding thousands of lines, so the tree opens
// with the areas folded and the shape of the work readable at a glance.
function leafActivityKeys(nodes: DraftActivity[], out: string[] = []): string[] {
  for (const a of nodes) {
    if (a.items.length > 0) out.push(a.key);
    leafActivityKeys(a.children, out);
  }
  return out;
}

// Every activity that has something under it - what "collapse all" folds.
function expandableKeys(nodes: DraftActivity[], out: string[] = []): string[] {
  for (const a of nodes) {
    if (a.items.length > 0 || a.children.length > 0) out.push(a.key);
    expandableKeys(a.children, out);
  }
  return out;
}

// toRows walks the tree in document order, skipping whatever hangs off a collapsed
// activity, and stamps each row with the depth and code it renders with.
function toRows(
  activities: DraftActivity[],
  collapsed: Set<string>,
  // Manual versions number activities by position; an import shows the workbook's own
  // codes and nothing where the sheet had none, rather than a "2" over a "3.2".
  positional: boolean,
  depth = 0,
  parentCode = "",
  // The parent row's own guides; a row's are its parent's plus one for itself. Top-level
  // rows have none - nothing above them to be joined to.
  parentLines: boolean[] = [],
  out: Row[] = [],
): Row[] {
  let seq = 0;
  activities.forEach((a, idx) => {
    // Blank rows are numbered only once they hold something, so the trailing new-activity
    // row shows no code until it is typed into - and the codes shown are the codes saved.
    const code = isBlank(a) ? "" : positional ? activityCode(parentCode, seq++) : (a.code ?? "");
    const lines = depth === 0 ? [] : [...parentLines, idx < activities.length - 1];
    out.push({
      key: a.key,
      kind: "activity",
      activity: a,
      code,
      depth,
      lines,
      expandable: a.items.length > 0 || a.children.length > 0,
    });
    if (collapsed.has(a.key)) return;
    // An activity's rows are its items and then its sub-activities, so an item's vertical
    // carries on whenever either another item or any sub-activity still follows it.
    a.items.forEach((item, j) => {
      out.push({
        key: item.key,
        kind: "item",
        activity: a,
        item,
        depth: depth + 1,
        lines: [...lines, j < a.items.length - 1 || a.children.length > 0],
      });
    });
    toRows(a.children, collapsed, positional, depth + 1, code, lines, out);
  });
  return out;
}

// One indent slot. `own` is the slot the row hangs off: it draws the elbow into the row,
// with the vertical running to the bottom only while more rows follow under the same
// activity. Every other slot is either a passing ancestor line or empty space.
function Guide({ own, running, reach }: { own: boolean; running: boolean; reach: number }) {
  const stroke: CSSProperties = { position: "absolute", background: GUIDE_COLOR };
  return (
    <span style={{ position: "relative", width: INDENT, flex: "none", alignSelf: "stretch" }}>
      {(own || running) && (
        <span
          style={{
            ...stroke,
            left: INDENT / 2,
            width: 1,
            top: -(CELL_PAD * 2),
            ...(own && !running ? { height: `calc(50% + ${CELL_PAD * 2}px)` } : { bottom: -(CELL_PAD * 2) }),
          }}
        />
      )}
      {/* Reaches past the slot and across the flex gap, up to whatever the row starts with. */}
      {own && <span style={{ ...stroke, left: INDENT / 2, top: "50%", width: reach, height: 1 }} />}
    </span>
  );
}

// mapActivities rebuilds the tree with one node replaced/removed. The tree is small (a WBS,
// not a dataset) so a full immutable rebuild per keystroke is cheaper than the bookkeeping
// an in-place update would need.
function mapActivities(nodes: DraftActivity[], key: string, fn: (a: DraftActivity) => DraftActivity | null): DraftActivity[] {
  const out: DraftActivity[] = [];
  for (const n of nodes) {
    if (n.key === key) {
      const replaced = fn(n);
      if (replaced) out.push(replaced);
      continue;
    }
    out.push({ ...n, children: mapActivities(n.children, key, fn) });
  }
  return out;
}

function stripKeys(activities: DraftActivity[], parentCode = ""): unknown[] {
  return activities.map((a, idx) => {
    const code = activityCode(parentCode, idx);
    return {
      id: a.id,
      code,
      name: a.name,
      items: a.items
        .filter((i) => i.itemId && i.approvedQty)
        .map((i) => ({ itemId: i.itemId, approvedQty: i.approvedQty, remarks: i.remarks ?? "" })),
      children: stripKeys(a.children, code),
    };
  });
}

function countItems(activities: DraftActivity[]): number {
  return activities.reduce((n, a) => n + a.items.length + countItems(a.children), 0);
}

// activityPathById flattens the tree to "1 - Substructure > 1.2 - Excavation" labels, for
// the imported view where rows are grouped under their resolved activity.
function activityPaths(nodes: BOMActivity[] = [], prefix = "", into = new Map<number, string>()) {
  for (const n of nodes) {
    const label = activityLabel(n);
    const path = prefix ? `${prefix} > ${label}` : label;
    into.set(n.id, path);
    activityPaths(n.children ?? [], path, into);
  }
  return into;
}

export default function BOMVersionDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: projects = [] } = useProjectsQuery();
  // The items this page can name: every line of the loaded version arrives with its item
  // attached, and a material picked while drafting is remembered here. The master itself
  // is never loaded - ItemSelect searches it on the server.
  const [pickedItems, setPickedItems] = useState<Item[]>([]);

  const isNew = id === "new";
  const projectId = isNew ? Number(searchParams.get("projectId")) : undefined;
  const [version, setVersion] = useState<ProjectBOMVersion | null>(null);
  const [activities, setActivities] = useState<DraftActivity[]>([]);
  // Collapsed rather than expanded, so an activity that has just been created - or one
  // that has just been given its first child - is open without anyone tracking its key.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState("");
  // Bumped on every reload so the history re-reads too: a mapping change writes a history
  // entry without touching anything on the version row that the history could key off.
  const [reloads, setReloads] = useState(0);
  // The review filter for an imported version: an import of a real workbook is thousands
  // of lines with the few that still need a decision scattered through them, so the table
  // opens on those alone and widens to everything once the user asks or nothing is left.
  const [onlyUnresolved, setOnlyUnresolved] = useState(true);
  const knownItems = useMemo(() => {
    const out: Item[] = [];
    const walk = (nodes: BOMActivity[] = []) =>
      nodes.forEach((a) => {
        (a.items ?? []).forEach((l) => l.item && out.push(l.item));
        walk(a.children ?? []);
      });
    walk(version?.activities ?? []);
    (version?.items ?? []).forEach((l) => l.item && out.push(l.item));
    return [...out, ...pickedItems];
  }, [version, pickedItems]);
  const knownById = useMemo(() => new Map(knownItems.map((it) => [it.id, it])), [knownItems]);

  async function load() {
    if (isNew) return;
    const res = await request<ProjectBOMVersion>({ url: `/bom-versions/${id}` });
    setVersion(res);
    setNotes(res.notes);
    const tree = toDraftActivities(res.activities ?? []);
    setActivities(tree);
    if (res.sourceType === "IMPORT") setCollapsed(new Set(leafActivityKeys(tree)));
    setReloads((n) => n + 1);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const project = isNew ? projects.find((p) => p.id === projectId) : version?.project;
  const canWrite = project && hasProjectGrant(user, "BOM.Write", project.id);
  const editable = isNew || version?.status === "DRAFT";
  const isImport = version?.sourceType === "IMPORT";
  const canEditTree = Boolean(editable && canWrite && !isImport);
  // An imported draft is reviewed row by row in the workbook's order, with a picker on
  // every line; once it leaves DRAFT the review is over and it reads as a tree like any
  // other version.
  const reviewing = Boolean(isImport && editable);

  // Keep one blank activity at the end of the root list: typing into it makes it a real
  // activity and this puts a fresh blank below it, so a new activity never needs a button.
  // Returning `prev` unchanged when the invariant already holds is what stops the loop.
  useEffect(() => {
    if (!canEditTree) return;
    setActivities((prev) => (prev.length > 0 && isBlank(prev[prev.length - 1]) ? prev : [...prev, blankActivity()]));
  }, [canEditTree, activities]);

  function toggleCollapsed(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  // Anything added under an activity has to be visible, so adding opens it first.
  function expand(key: string) {
    setCollapsed((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  // Only sub-activities are added by hand; a root activity is created by typing into the
  // blank row the effect above keeps at the bottom.
  function addSubActivity(parentKey: string) {
    setActivities((prev) =>
      mapActivities(prev, parentKey, (a) => ({ ...a, children: [...a.children, blankActivity()] })),
    );
    expand(parentKey);
  }

  function updateActivity(key: string, patch: Partial<DraftActivity>) {
    setActivities((prev) => mapActivities(prev, key, (a) => ({ ...a, ...patch })));
  }

  function removeActivity(key: string) {
    setActivities((prev) => mapActivities(prev, key, () => null));
  }

  function addItem(activityKey: string) {
    setActivities((prev) =>
      mapActivities(prev, activityKey, (a) => ({ ...a, items: [...a.items, { key: nextKey("itm") }] })),
    );
    expand(activityKey);
  }

  function updateItem(activityKey: string, itemKey: string, patch: Partial<DraftItem>) {
    setActivities((prev) =>
      mapActivities(prev, activityKey, (a) => ({
        ...a,
        items: a.items.map((i) => (i.key === itemKey ? { ...i, ...patch } : i)),
      })),
    );
  }

  function removeItem(activityKey: string, itemKey: string) {
    setActivities((prev) =>
      mapActivities(prev, activityKey, (a) => ({ ...a, items: a.items.filter((i) => i.key !== itemKey) })),
    );
  }

  async function save() {
    // Blank rows are dropped rather than rejected - only an activity that holds something
    // and still has no name is an error, at any depth.
    const kept = prune(activities);
    const unnamed = (nodes: DraftActivity[]): boolean =>
      nodes.some((a) => !a.name.trim() || unnamed(a.children));
    if (unnamed(kept)) {
      message.error("Every activity needs a name.");
      return;
    }
    const payload = {
      projectId: isNew ? projectId : version!.projectId,
      notes,
      activities: stripKeys(kept),
    };
    if (isNew) {
      const created = await request<ProjectBOMVersion>({ method: "POST", url: "/bom-versions", data: payload });
      message.success("BOM created.");
      navigate(`/bom-versions/${created.id}`, { replace: true });
    } else {
      await request({ method: "PUT", url: `/bom-versions/${id}`, data: payload });
      message.success("BOM updated.");
      load();
    }
  }

  async function submit() {
    await request({ method: "POST", url: `/bom-versions/${id}/submit` });
    message.success("Submitted for approval.");
    load();
  }

  async function revise() {
    const created = await request<ProjectBOMVersion>({ method: "POST", url: `/bom-versions/${id}/revise` });
    message.success("New draft version created.");
    navigate(`/bom-versions/${created.id}`);
  }

  async function mapItem(itemRowId: number, patch: { itemId?: number; mapping?: string }) {
    await request({ method: "PUT", url: `/bom-versions/${id}/items/${itemRowId}/map`, data: patch });
    load();
  }

  // A unit-incompatible row already names its item; what is missing is QS's word that the
  // quantity may be read in the master's unit. Re-mapping the row to the same item is that
  // word (bom_import.go's handleMapBOMItem marks it MAPPED without re-checking the unit),
  // and since a workbook repeats the same unit for the same material on every line, the
  // same decision can be applied to all of that item's mismatched rows in one go.
  async function acceptUnit(rows: BOMItem[]) {
    await Promise.all(
      rows
        .filter((l) => l.itemId != null)
        .map((l) => request({ method: "PUT", url: `/bom-versions/${id}/items/${l.id}/map`, data: { itemId: l.itemId } })),
    );
    load();
  }

  if (!isNew && !version) return null;

  const isUnresolved = (l: BOMItem) => l.mapping === "UNMAPPED" || l.mapping === "UNIT_INCOMPATIBLE";
  const unresolvedCount = (version?.items ?? []).filter(isUnresolved).length;
  const showOnlyUnresolved = onlyUnresolved && unresolvedCount > 0;
  const paths = activityPaths(version?.activities ?? []);
  const itemLabel = (itemId?: number) => {
    const it = itemId != null ? knownById.get(itemId) : undefined;
    return it ? fmtItem(it) : `Item #${itemId}`;
  };

  return (
    <div>
      <PageHeader
        title={isNew ? "New BOM" : `${version!.docNo} (v${version!.versionNo})`}
        subtitle={<ProjectLink project={project} />}
        extra={
          <Space>
            {version && (
              <span data-tour="doc-status">
                <DocumentStatusTag status={version.status} />
              </span>
            )}
            {isNew && projectId && (
              <Button onClick={() => navigate(`/projects/${projectId}?tab=bom`)}>Import from Excel instead</Button>
            )}
            {canEditTree && (
              <Button type="primary" onClick={save} loading={loading}>
                Save
              </Button>
            )}
            {!isNew && version?.status === "DRAFT" && canWrite && (
              <Button onClick={submit} loading={loading} disabled={unresolvedCount > 0} data-tour="bom-submit">
                Submit for approval
              </Button>
            )}
            {!isNew && version?.status === "APPROVED_ACTIVE" && canWrite && (
              <>
                <Button onClick={revise} loading={loading}>
                  Revise
                </Button>
                <Button onClick={() => navigate(`/bom-variations/new?projectId=${version.projectId}`)} data-tour="bom-raise-variation">
                  Raise variation
                </Button>
              </>
            )}
          </Space>
        }
      />
      {!isNew && isImport && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">
            Imported from {version!.workbookName || "a workbook"}.{" "}
            {unresolvedCount > 0 ? `${unresolvedCount} row(s) still need mapping.` : "All rows resolved."}
          </Typography.Text>
        </Card>
      )}
      <Card size="small" title="Notes" style={{ marginBottom: 16 }}>
        <Input.TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!editable} />
      </Card>

      {!reviewing ? (
        <Card
          size="small"
          title="Activities and items"
          extra={
            activities.some((a) => a.items.length > 0 || a.children.length > 0) && (
              <Space size={0}>
                <Button type="link" size="small" onClick={() => setCollapsed(new Set())}>
                  Expand all
                </Button>
                <Button type="link" size="small" onClick={() => setCollapsed(new Set(expandableKeys(activities)))}>
                  Collapse all
                </Button>
              </Space>
            )
          }
        >
          {activities.length === 0 ? (
            <Typography.Text type="secondary">No activities.</Typography.Text>
          ) : (
            <Table<Row>
              rowKey="key"
              size="small"
              pagination={false}
              scroll={{ x: "max-content" }}
              dataSource={toRows(activities, collapsed, !isImport)}
              columns={[
                {
                  title: "Activity / item",
                  render: (_: unknown, row: Row) => {
                    // Every row is one flex line: guides, expand toggle, then the row's own
                    // controls - so the toggle sits on the vertical centre of the inputs
                    // beside it and the two kinds of row share one left edge. A material's
                    // quantity and remarks ride on that same line, right after the material
                    // they belong to, rather than in columns of their own an activity row
                    // leaves empty.
                    const isCollapsed = collapsed.has(row.key);
                    const hasToggle = row.kind === "activity" && row.expandable;
                    // The elbow stops at the toggle it joins to; where the toggle is only
                    // reserved space (items, and activities with nothing under them yet) it
                    // carries on across that space to the row's first control instead.
                    const reach = INDENT / 2 + ROW_GAP + (hasToggle ? 0 : TOGGLE_WIDTH + ROW_GAP);
                    const unit =
                      row.kind === "item" && row.item.itemId != null ? knownById.get(row.item.itemId)?.unit?.abbreviation : undefined;
                    const toggle =
                      hasToggle && row.kind === "activity" ? (
                        <Button
                          type="text"
                          size="small"
                          style={{ width: TOGGLE_WIDTH }}
                          icon={isCollapsed ? <PlusSquareOutlined /> : <MinusSquareOutlined />}
                          aria-label={isCollapsed ? "Expand activity" : "Collapse activity"}
                          onClick={() => toggleCollapsed(row.key)}
                        />
                      ) : (
                        <span style={{ display: "inline-block", width: TOGGLE_WIDTH, flex: "none" }} />
                      );
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: ROW_GAP }}>
                        {row.lines.map((running, i) => (
                          <Guide key={i} own={i === row.lines.length - 1} running={running} reach={reach} />
                        ))}
                        {toggle}
                        {row.kind === "activity" ? (
                          <>
                            {/* Fixed slot so the blank row's empty code still lines its
                                name input up with the rows above it. */}
                            <span style={{ display: "inline-block", width: CODE_WIDTH, flex: "none" }}>
                              {row.code && <Tag style={{ marginInlineEnd: 0 }}>{row.code}</Tag>}
                            </span>
                            {canEditTree ? (
                              <>
                                <Input
                                  style={{ width: 260 }}
                                  placeholder="Activity name"
                                  value={row.activity.name}
                                  onChange={(e) => updateActivity(row.activity.key, { name: e.target.value })}
                                />
                                <Tooltip title="Add item">
                                  <Button
                                    size="small"
                                    icon={<PlusOutlined />}
                                    aria-label="Add item"
                                    onClick={() => addItem(row.activity.key)}
                                  />
                                </Tooltip>
                                <Tooltip title="Add sub-activity">
                                  <Button
                                    size="small"
                                    icon={<SubnodeOutlined />}
                                    aria-label="Add sub-activity"
                                    onClick={() => addSubActivity(row.activity.key)}
                                  />
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <strong>{row.activity.name}</strong>
                                {row.activity.description && (
                                  <Typography.Text type="secondary">{row.activity.description}</Typography.Text>
                                )}
                              </>
                            )}
                          </>
                        ) : canEditTree ? (
                          <>
                            <ItemSelect
                              style={{ width: 350 }}
                              placeholder="Select a material"
                              value={row.item.itemId}
                              knownItems={knownItems}
                              onChange={(v, it) => {
                                if (it) setPickedItems((prev) => (prev.some((p) => p.id === it.id) ? prev : [...prev, it]));
                                updateItem(row.activity.key, row.item.key, { itemId: typeof v === "number" ? v : undefined });
                              }}
                            />
                            <span style={{ width: 180, flex: "none" }}>
                              <QtyInput
                                value={row.item.approvedQty}
                                unit={unit}
                                placeholder="Approved qty"
                                onChange={(v) =>
                                  updateItem(row.activity.key, row.item.key, { approvedQty: v ?? undefined })
                                }
                              />
                            </span>
                            <Input
                              style={{ width: 240 }}
                              placeholder="Remarks"
                              value={row.item.remarks}
                              onChange={(e) => updateItem(row.activity.key, row.item.key, { remarks: e.target.value })}
                            />
                          </>
                        ) : (
                          <>
                            <span>{row.item.itemId != null ? itemLabel(row.item.itemId) : row.item.nameRaw}</span>
                            <span>
                              {row.item.approvedQty ?? 0} {unit ?? row.item.unitRaw ?? ""}
                            </span>
                            {row.item.mapping && row.item.mapping !== "MAPPED" && (
                              <Tag color={MAPPING_TAG[row.item.mapping].color} style={{ marginInlineEnd: 0 }}>
                                {MAPPING_TAG[row.item.mapping].label}
                              </Tag>
                            )}
                            {row.item.remarks && <Typography.Text type="secondary">{row.item.remarks}</Typography.Text>}
                          </>
                        )}
                      </div>
                    );
                  },
                },
                ...(canEditTree
                  ? [
                      {
                        title: "",
                        width: 60,
                        render: (_: unknown, row: Row) =>
                          row.kind === "activity" ? (
                            // Nothing to remove on the blank row, and it would come straight back.
                            !isBlank(row.activity) && (
                              <Popconfirm
                                title="Remove this activity and everything under it?"
                                onConfirm={() => removeActivity(row.activity.key)}
                              >
                                <Tooltip title="Remove activity">
                                  <Button size="small" danger icon={<DeleteOutlined />} />
                                </Tooltip>
                              </Popconfirm>
                            )
                          ) : (
                            <Tooltip title="Remove item">
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeItem(row.activity.key, row.item.key)}
                              />
                            </Tooltip>
                          ),
                      },
                    ]
                  : []),
              ]}
            />
          )}
          {/* prune, not activities: the trailing blank row is not an activity yet. */}
          {canEditTree && countItems(activities) === 0 && prune(activities).length > 0 && (
            <Typography.Text type="secondary">Add at least one item before saving.</Typography.Text>
          )}
        </Card>
      ) : (
        <Card
          size="small"
          title="Activities and items"
          data-tour="bom-review"
          extra={
            unresolvedCount > 0 && (
              <Checkbox checked={showOnlyUnresolved} onChange={(e) => setOnlyUnresolved(e.target.checked)} data-tour="bom-only-unresolved">
                Only rows needing attention ({unresolvedCount})
              </Checkbox>
            )
          }
        >
          {unresolvedCount > 0 && editable && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Resolve every unmapped or unit-incompatible row before this version can be submitted."
            />
          )}
          <Table
            rowKey="id"
            size="small"
            dataSource={showOnlyUnresolved ? (version?.items ?? []).filter(isUnresolved) : (version?.items ?? [])}
            scroll={{ x: "max-content" }}
            // A real workbook runs to thousands of lines, each row carrying a picker; a
            // single unpaginated table of them takes seconds to lay out and every keystroke
            // in a picker pays for the lot.
            pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: [50, 100, 250, 500] }}
            columns={[
              { title: "Row", dataIndex: "rowNumber", width: 60 },
              {
                title: "Activity",
                width: 220,
                render: (_: unknown, l: BOMItem) => paths.get(l.activityId) ?? l.activityRaw,
              },
              { title: "Item code", dataIndex: "itemCodeRaw", width: 140 },
              { title: "Name", dataIndex: "itemNameRaw" },
              {
                title: "Unit",
                width: 110,
                render: (_: unknown, l: BOMItem) => {
                  const master = l.item?.unit?.abbreviation;
                  if (l.mapping !== "UNIT_INCOMPATIBLE" || !master) return l.unitRaw;
                  return (
                    <Tooltip title={l.mappingNote}>
                      <Typography.Text type="warning">{l.unitRaw}</Typography.Text>
                      <div>
                        <Typography.Text type="secondary">master: {master}</Typography.Text>
                      </div>
                    </Tooltip>
                  );
                },
              },
              { title: "Qty", dataIndex: "approvedQty", width: 90 },
              {
                title: "Status",
                dataIndex: "mapping",
                width: 130,
                render: (v: BOMItem["mapping"], l: BOMItem) => (
                  <Tooltip title={l.mappingNote || undefined}>
                    <Tag color={MAPPING_TAG[v].color}>{MAPPING_TAG[v].label}</Tag>
                  </Tooltip>
                ),
              },
              // Read-only reviewers see the status column and nothing to act with.
              ...(!canWrite
                ? []
                : [
                    {
                      title: "Mapping",
                      key: "mapping",
                      width: 320,
                      render: (_: unknown, l: BOMItem) => {
                        const master = l.item?.unit?.abbreviation;
                        const sameItemRows = (version?.items ?? []).filter(
                          (o) => o.mapping === "UNIT_INCOMPATIBLE" && o.itemId != null && o.itemId === l.itemId,
                        );
                        return (
                          <Space direction="vertical" size={2}>
                            <ItemSelect
                              style={{ width: 300 }}
                              value={l.itemId ?? l.mapping}
                              knownItems={knownItems}
                              extraOptions={MAPPING_OPTIONS}
                              onChange={(v) => {
                                if (typeof v === "number") mapItem(l.id, { itemId: v });
                                else if (v) mapItem(l.id, { mapping: v });
                              }}
                            />
                            {l.mapping === "UNIT_INCOMPATIBLE" && master && (
                              <Space size={4} wrap>
                                <Button size="small" type="link" style={{ padding: 0 }} onClick={() => acceptUnit([l])}>
                                  Accept in {master}
                                </Button>
                                {sameItemRows.length > 1 && (
                                  <Button size="small" type="link" style={{ padding: 0 }} onClick={() => acceptUnit(sameItemRows)}>
                                    (all {sameItemRows.length} rows of {l.item?.code})
                                  </Button>
                                )}
                              </Space>
                            )}
                          </Space>
                        );
                      },
                    },
                  ]),
            ]}
          />
        </Card>
      )}
      {!isNew && <ApprovalTimeline docType="BOM_VERSION" docId={Number(id)} refreshKey={version?.status} onActed={load} />}
      {!isNew && <BOMVersionHistory versionId={Number(id)} refreshKey={reloads} />}
    </div>
  );
}
